import { useContext, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressaoPanvel } from "@/components/Personalizados/ImpressaoPanvelComponent";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  InfoComplField,
  aplicarPreDefinicoesUnicas,
  infoComplObrigatoriasFaltando,
  parseInfoComplObrigatorios,
} from "@/components/InfoComplField";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { alert } from "@/contexts/AlertModalContext";

import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import { categoriaComboValue } from "@/utils/UtilFuncoes";

import {
  Check,
  ChevronsUpDown,
  Loader2,
  MapPin,
  Package,
  Plus,
  Printer,
  RefreshCw,
  Tag,
} from "lucide-react";

const novaCategoriaPadrao = (id_conta: string): NewCategoriaItem => ({
  descricao: "",
  ean: "",
  inf_compl_1: "",
  inf_compl_2: "",
  inf_compl_3: "",
  inf_compl_4: "",
  inf_compl_5: "",
  inf_compl_6: "",
  inf_compl_7: "",
  inf_compl_8: "",
  inf_compl_9: "",
  inf_compl_10: "",
  inf_compl_11: "",
  inf_compl_12: "",
  id_conta,
});

function ImpressaoPanvel() {
  const { isLoading } = useGlobalLoader();
  const { adicionarItensNaFila, estaImprimindo } = useImpressaoPanvel();
  const { categorias, coletaCategorias, criarCategorias } = useCategorias();
  const { coletaSeriais } = useSeriais();
  const { localizacoes, coletaLocalizacoes } = useLocalizacoes();
  const { impressaoConfig } = useImpressaoConfig();
  const { contaConfig } = useContaConfig();
  const { requireAdmin } = useContext(AdminContext)!;

  const [mostarMaisInfos, setMostarMaisInfos] = useState(false);
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const [comboCodigo, setComboCodigo] = useState(false);
  const [gerouListaImpressao, setGerouListaImpressao] = useState(false);

  const [categoriaSelecionada, setCategoriaSelecionada] = useState<
    CategoriaItemResponse | undefined
  >(undefined);

  // Nova categoria via Dialog
  const [novaCategoriaOpen, setNovaCategoriaOpen] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState<NewCategoriaItem>(
    novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
  );

  // ---- Preferências (chaves consistentes) ----
  useEffect(() => {
    const mostrarMais = localStorage.getItem("mostrarMaisInfos");
    setMostarMaisInfos(mostrarMais === "true");
    const repetidos = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(repetidos === "true");
  }, []);

  // ---- Localização padrão ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
    if (gerouListaImpressao) setGerouListaImpressao(false);
  }, [impressaoConfig, localizacoes, gerouListaImpressao]);

  // ---- Nova categoria ----
  const abrirNovaCategoria = () => {
    setComboCodigo(false);
    setNovaCategoria(
      aplicarPreDefinicoesUnicas(
        impressaoConfig,
        novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
      ),
    );
    setNovaCategoriaOpen(true);
  };

  const handleCriarCategoria = async () => {
    if (!novaCategoria.descricao || !novaCategoria.ean) {
      alert.error("Descrição e código são obrigatórios.");
      return;
    }
    if (isLoading) return;

    try {
      const novaCategoriaCriada = await criarCategorias([
        { ...novaCategoria, id_conta: impressaoConfig?.id_conta || "" },
      ]);
      if (novaCategoriaCriada && novaCategoriaCriada.length !== 0) {
        setCategoriaSelecionada(
          aplicarPreDefinicoesUnicas(impressaoConfig, novaCategoriaCriada[0]),
        );
        alert.success("Categoria criada com sucesso!");
      }
      setNovaCategoria(
        aplicarPreDefinicoesUnicas(
          impressaoConfig,
          novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
        ),
      );
      setNovaCategoriaOpen(false);
      await coletaCategorias();
    } catch {
      alert.error("Erro ao criar categoria.");
    }
  };

  // ---- Sincronizar ----
  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setCategoriaSelecionada(undefined);
    alert.success("Dados sincronizados com sucesso!");
  };

  // ---- Imprimir ----
  const gerarListaParaImpressao = async () => {
    if (!categoriaSelecionada) {
      alert.error("Selecione uma categoria para impressão.");
      return;
    }
    if (!impressaoConfig) {
      alert.error("Configuração de impressão não encontrada.");
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error("Selecione uma localização para impressão.");
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(
        "Informe o Hardware ID nas configurações para realizar a impressão.",
      );
      return;
    }
    const faltandoObrigatorios = infoComplObrigatoriasFaltando(
      impressaoConfig,
      categoriaSelecionada,
    );
    if (faltandoObrigatorios.length > 0) {
      const nomes = faltandoObrigatorios.map(
        (idx) =>
          (contaConfig as any)?.[`info_compl_${idx}`] || `Info. Compl. ${idx}`,
      );
      alert.error(
        `Preencha os campos obrigatórios antes de imprimir: ${nomes.join(", ")}.`,
      );
      return;
    }
    adicionarItensNaFila(
      categoriaSelecionada,
      localizacaoSelecionada,
      localizacaoSelecionada2,
      localizacaoSelecionada3,
      localizacaoSelecionada4,
    );
    setCategoriaSelecionada(undefined);
    setGerouListaImpressao(true);
  };

  if (!impressaoConfig) return null;

  // Lista de campos info complementar — evita 10 cópias do JSX
  const obrigatoriosInfoCompl = new Set(
    parseInfoComplObrigatorios(impressaoConfig.info_compl_obrigatorios),
  );
  const camposInfoCompl: { idx: number; label: string; obrigatorio: boolean }[] =
    [];
  for (let i = 1; i <= 10; i++) {
    camposInfoCompl.push({
      idx: i,
      label:
        (contaConfig as any)?.[`info_compl_${i}`] || `Info. Complementar ${i}`,
      obrigatorio: obrigatoriosInfoCompl.has(i),
    });
  }

  const camposPrimarios = camposInfoCompl.slice(0, 4);
  const camposExtras = camposInfoCompl.slice(4);

  // Só exibimos na listagem categorias que tenham descrição E código (ean).
  // Categorias sem código/ean ou sem descrição não aparecem na seleção.
  const categoriasValidas = categorias.filter(
    (c) => !!c.descricao?.trim() && !!c.ean?.trim(),
  );

  const podeImprimir =
    !!categoriaSelecionada && !!localizacaoSelecionada && !estaImprimindo;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Impressão por Bipagem
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Bipe o código de barras do item e a etiqueta é impressa automaticamente
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="panvel-mostrar-mais"
              disabled={isLoading}
              checked={mostarMaisInfos}
              onCheckedChange={(checked) => {
                setMostarMaisInfos(checked);
                localStorage.setItem("mostrarMaisInfos", String(checked));
              }}
            />
            <Label
              htmlFor="panvel-mostrar-mais"
              className="text-xs cursor-pointer"
            >
              Mais campos
            </Label>
          </div>

          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="panvel-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label
              htmlFor="panvel-repetidos"
              className="text-xs cursor-pointer"
            >
              Permitir repetidos
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            size="sm"
            onClick={() => {
              if (!estaImprimindo) gerarListaParaImpressao();
            }}
            disabled={!podeImprimir || isLoading}
            title={
              estaImprimindo
                ? "Aguarde a impressão atual finalizar"
                : !categoriaSelecionada
                  ? "Selecione uma categoria para imprimir"
                  : !localizacaoSelecionada
                    ? "Selecione uma localização para imprimir"
                    : "Imprimir"
            }
          >
            {estaImprimindo ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">
              {estaImprimindo ? "Imprimindo..." : "Imprimir"}
            </span>
          </Button>
        </div>
      </div>

      {/* 1. Categoria */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium">Categoria</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2">
            <Label htmlFor="panvel-codigo">
              Código <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Popover open={comboCodigo} onOpenChange={setComboCodigo}>
                <PopoverTrigger asChild>
                  <Button
                    id="panvel-codigo"
                    variant="outline"
                    role="combobox"
                    className="flex-1 justify-between font-normal"
                  >
                    {categoriaSelecionada ? (
                      <span className="truncate">
                        <span className="font-medium">
                          {categoriaSelecionada.descricao}
                        </span>
                        <span className="text-muted-foreground ml-2">
                          {categoriaSelecionada.ean}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        Selecione uma categoria
                      </span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-0 w-(--radix-popover-trigger-width)"
                >
                  <Command className="rounded-lg">
                    <CommandInput placeholder="Buscar por descrição, código ou info. complementar..." />
                    <CommandList>
                      <CommandEmpty>
                        <div className="text-xs text-muted-foreground p-2">
                          Nenhuma categoria encontrada
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {categoriasValidas.map((categoria, index) => (
                          <CommandItem
                            key={index}
                            value={categoriaComboValue(categoria)}
                            onSelect={(currentValue) => {
                              const ean = currentValue.split("|-|")[0].trim();
                              const sel = categorias.find(
                                (c) => c.ean === ean,
                              );
                              setCategoriaSelecionada(
                                sel
                                  ? aplicarPreDefinicoesUnicas(
                                      impressaoConfig,
                                      sel,
                                    )
                                  : sel,
                              );
                              setComboCodigo(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                categoriaSelecionada?.ean === categoria.ean
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium truncate">
                                {categoria.descricao}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {categoria.ean}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    <div className="border-t p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={abrirNovaCategoria}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Nova categoria
                      </Button>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="icon"
                onClick={abrirNovaCategoria}
                title="Criar nova categoria"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {categoriaSelecionada ? (
        <>
          {/* 2. Localizações */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Localizações
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="panvel-loc-1"
                    className="text-xs text-muted-foreground"
                  >
                    Nível 1 <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    disabled={isLoading}
                    value={localizacaoSelecionada}
                    onValueChange={(value) => {
                      setLocalizacaoSelecionada(value);
                      setLocalizacaoSelecionada2("");
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="panvel-loc-1" className="w-full">
                      <SelectValue placeholder="Não definido" />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes.map((l) => (
                        <SelectItem key={l._id} value={l._id}>
                          {l.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="panvel-loc-2"
                    className="text-xs text-muted-foreground"
                  >
                    Nível 2
                  </Label>
                  <Select
                    disabled={isLoading || !localizacaoSelecionada}
                    value={localizacaoSelecionada2}
                    onValueChange={(v) => {
                      setLocalizacaoSelecionada2(v);
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="panvel-loc-2" className="w-full">
                      <SelectValue placeholder="Não definido" />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.map((n2) => (
                          <SelectItem key={n2._id} value={n2._id}>
                            {n2.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="panvel-loc-3"
                    className="text-xs text-muted-foreground"
                  >
                    Nível 3
                  </Label>
                  <Select
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2
                    }
                    value={localizacaoSelecionada3}
                    onValueChange={(v) => {
                      setLocalizacaoSelecionada3(v);
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="panvel-loc-3" className="w-full">
                      <SelectValue placeholder="Não definido" />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.find(
                          (n2) => n2._id === localizacaoSelecionada2,
                        )
                        ?.children.map((n3) => (
                          <SelectItem key={n3._id} value={n3._id}>
                            {n3.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="panvel-loc-4"
                    className="text-xs text-muted-foreground"
                  >
                    Nível 4
                  </Label>
                  <Select
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2 ||
                      !localizacaoSelecionada3
                    }
                    value={localizacaoSelecionada4}
                    onValueChange={setLocalizacaoSelecionada4}
                  >
                    <SelectTrigger id="panvel-loc-4" className="w-full">
                      <SelectValue placeholder="Não definido" />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.find(
                          (n2) => n2._id === localizacaoSelecionada2,
                        )
                        ?.children.find(
                          (n3) => n3._id === localizacaoSelecionada3,
                        )
                        ?.children.map((n4) => (
                          <SelectItem key={n4._id} value={n4._id}>
                            {n4.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Dados do item */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    3
                  </div>
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    Dados do item
                  </CardTitle>
                </div>
                {categoriaSelecionada.ean && (
                  <Badge variant="secondary" className="">
                    {categoriaSelecionada.ean}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label htmlFor="panvel-descricao">
                  Descrição <span className="text-destructive">*</span>
                </Label>
                <Input
                  disabled={isLoading}
                  id="panvel-descricao"
                  type="text"
                  value={categoriaSelecionada?.descricao || ""}
                  onChange={(e) =>
                    setCategoriaSelecionada({
                      ...categoriaSelecionada!,
                      descricao: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {camposPrimarios.map((c) => (
                  <div key={c.idx} className="grid gap-2">
                    <Label
                      htmlFor={`panvel-info-${c.idx}`}
                      className="text-xs text-muted-foreground"
                    >
                      {c.label}
                      {c.obrigatorio && (
                        <span className="text-destructive ml-0.5">*</span>
                      )}
                    </Label>
                    <InfoComplField
                      idx={c.idx}
                      disabled={isLoading}
                      id={`panvel-info-${c.idx}`}
                      value={
                        ((categoriaSelecionada as any)?.[
                          `inf_compl_${c.idx}`
                        ] as string) || ""
                      }
                      onChange={(v) =>
                        setCategoriaSelecionada({
                          ...categoriaSelecionada!,
                          [`inf_compl_${c.idx}`]: v,
                        } as CategoriaItemResponse)
                      }
                    />
                  </div>
                ))}
              </div>

              {mostarMaisInfos && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t">
                  {camposExtras.map((c) => (
                    <div key={c.idx} className="grid gap-2">
                      <Label
                        htmlFor={`panvel-info-${c.idx}`}
                        className="text-xs text-muted-foreground"
                      >
                        {c.label}
                        {c.obrigatorio && (
                          <span className="text-destructive ml-0.5">*</span>
                        )}
                      </Label>
                      <InfoComplField
                        idx={c.idx}
                        disabled={isLoading}
                        id={`panvel-info-${c.idx}`}
                        value={
                          ((categoriaSelecionada as any)?.[
                            `inf_compl_${c.idx}`
                          ] as string) || ""
                        }
                        onChange={(v) =>
                          setCategoriaSelecionada({
                            ...categoriaSelecionada!,
                            [`inf_compl_${c.idx}`]: v,
                          } as CategoriaItemResponse)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Tag className="h-6 w-6" />
            </div>
            <div className="text-center max-w-sm">
              <p className="text-sm font-medium text-foreground">
                Selecione uma categoria para começar
              </p>
              <p className="text-xs">
                Escolha uma categoria existente acima ou crie uma nova. Os
                campos do item e a impressão ficarão disponíveis aqui.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: nova categoria */}
      <Dialog open={novaCategoriaOpen} onOpenChange={setNovaCategoriaOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova categoria</DialogTitle>
            <DialogDescription>
              Crie uma nova categoria para usar nesse fluxo de impressão.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-2">
            <div className="grid gap-2">
              <Label htmlFor="panvel-nova-cat-descricao">
                Descrição <span className="text-destructive">*</span>
              </Label>
              <Input
                autoComplete="off"
                id="panvel-nova-cat-descricao"
                value={novaCategoria.descricao}
                onChange={(e) =>
                  setNovaCategoria({
                    ...novaCategoria,
                    descricao: e.target.value,
                  })
                }
                placeholder="Ex: Caixa Genérico 30 comprimidos"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="panvel-nova-cat-ean">
                Código <span className="text-destructive">*</span>
              </Label>
              <Input
                autoComplete="off"
                id="panvel-nova-cat-ean"
                value={novaCategoria.ean}
                onChange={(e) =>
                  setNovaCategoria({ ...novaCategoria, ean: e.target.value })
                }
                placeholder="EAN ou código"
                className=""
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="grid gap-2">
                  <Label
                    htmlFor={`panvel-nova-cat-info-${i}`}
                    className="text-xs text-muted-foreground"
                  >
                    {(contaConfig as any)?.[`info_compl_${i}`] ||
                      `Info. Complementar ${i}`}
                  </Label>
                  <InfoComplField
                    idx={i}
                    id={`panvel-nova-cat-info-${i}`}
                    value={
                      ((novaCategoria as any)[`inf_compl_${i}`] as string) || ""
                    }
                    onChange={(v) =>
                      setNovaCategoria({
                        ...novaCategoria,
                        [`inf_compl_${i}`]: v,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNovaCategoriaOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleCriarCategoria}
              disabled={isLoading}
            >
              <Plus className="h-4 w-4" />
              Criar categoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ImpressaoPanvel;
